import { Typography } from "@mui/material";
import { get, memoize } from "lodash";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import AppBar from "src/components/AppBar/AppBar";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import GuardedComponent from "src/components/GuardedComponent/GuardedComponent";
import Icon, { IconName } from "src/components/Icon/Icon";
import FilterContainer from "src/components/TableV2/FilterContainer";
import { TableActions } from "src/components/TableV2/TableActions";
import Title from "src/components/TableV2/Title";
import { LoadingContextType } from "src/hooks/useLoading";
import { UserRole } from "src/store/models/user.model";
import { fetchProductBuilderOptions } from "src/store/reducers/productBuilder";
import { openUrl } from "src/util/util.navigation";
import SearchFieldTool from "../../components/TableV2/SearchField";
import DisTable from "../../components/TableV2/Table";
import * as S from "../../constants/StringConstants";
import { IProductV2 } from "../../store/models/product.model";
import { IRootState } from "../../store/reducers";
import { fetchProductsList, removeProduct } from "../../store/reducers/productsV2";

interface IProductsProps extends StateProps, DispatchProps, RouteComponentProps, LoadingContextType {}

interface IProductsState {
  deletedId: number | null;
}

const getMap = memoize(list => list.reduce((map, { key, value }) => ({ ...map, [value]: key }), {}));

export class Products extends React.Component<IProductsProps, IProductsState> {
  constructor(props: IProductsProps) {
    super(props);
    this.state = { deletedId: null };

    this.handleDeleteProductConfirm = this.handleDeleteProductConfirm.bind(this);
    this.handleDeleteProductCancel = this.handleDeleteProductCancel.bind(this);
  }

  async componentDidMount() {
    await this.props.fetchProductBuilderOptions();
  }

  handleDeleteProductPopup = productId => {
    this.setState({ deletedId: productId });
  };

  async handleDeleteProductConfirm() {
    const { deletedId } = this.state;
    if (deletedId === null) return;

    try {
      await this.props.removeProduct(deletedId);
      await this.props.fetchProductsList();
    } finally {
      this.setState({ deletedId: null });
    }
  }

  handleDeleteProductCancel() {
    this.setState({ deletedId: null });
  }

  render() {
    const {
      fetchProductsList,
      productBuilder: pb,
      products: { map, projection, total, listOptions, loading },
    } = this.props;

    const columns = [
      {
        key: "productType",
        title: S.PROD_COL_PRODUCT_TYPE,
        render: (p: IProductV2) => get(getMap(pb.productTypeList), p.productTypeID, ""),
        style: { minWidth: "12rem" },
      },
      {
        key: "carrierType",
        title: S.PROD_COL_CARRIER_TYPE,
        render: (p: IProductV2) => get(getMap(pb.carrierTypeList), p.carrierTypeID, ""),
        style: { minWidth: "12rem", paddingLeft: "1rem" },
      },
      {
        key: "designType",
        title: S.PROD_COL_ACTIONS,
        render: (p: IProductV2) => get(getMap(pb.designTypeList), p.designTypeID, ""),
        style: { minWidth: "7rem" },
      },
      {
        key: "actions",
        title: S.BRO_COL_ACTIONS,
        style: { minWidth: "8rem" },
        render: (item: IProductV2) => {
          return (
            <TableActions
              actions={[
                {
                  label: S.PROD_DELETE_BUTTON,
                  handler: () => this.handleDeleteProductPopup(item.id),
                },
              ]}
            />
          );
        },
      },
    ];

    return (
      <div className="table-view-v2__content">
        <div className="table-view__header-buttons-container">
          <AppBar icon={IconName.ProductBuilder} title={S.PROD_PAGE_TITLE} loading={loading}>
            <div className="table-view__buttons-container">
              <GuardedComponent authorizedRoles={[UserRole.Admin]}>
                <button className="side-nav__add-button" onClick={() => openUrl("/products/new")}>
                  <Icon name={IconName.Plus} />
                  {S.NEW_PRODUCT}
                </button>
              </GuardedComponent>
            </div>
          </AppBar>
        </div>

        <DisTable<IProductV2>
          columns={columns}
          initialState={{ rows: 25, map, projection, total, ...listOptions }}
          onRowClick={(data: IProductV2) => {
            this.props.history.push(`/products/${data.id}`);
          }}
          onUpdate={async ({ page, rows, sort, query }) => {
            const result: any = await fetchProductsList({ page, rows, sort, query });

            return {
              total: get(result, "value.data.totalRows") as number,
              data: get(result, "value.data.items") as IProductV2[],
            };
          }}>
          <FilterContainer
            left={<Title text={S.PROD_PAGE_TITLE} />}
            right={
              <>
                <SearchFieldTool />
              </>
            }></FilterContainer>
        </DisTable>

        {this.state.deletedId !== null && (
          <PromptModal
            open={this.state.deletedId !== null}
            title={S.PROD_DELETE_CONFIRM_TITLE}
            confirmText={S.PROD_DELETE_BUTTON}
            rejectText={S.PROD_CANCEL_BUTTON}
            onConfirm={this.handleDeleteProductConfirm}
            onReject={this.handleDeleteProductCancel}>
            <Typography variant="h6">{S.PROD_CONFIRM_DELETE}</Typography>
          </PromptModal>
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ productsV2: products, productBuilder }: IRootState) => ({ products, productBuilder });
const mapDispatchToProps = { fetchProductsList, removeProduct, fetchProductBuilderOptions };

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Products));
